import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { HouseholdsService } from '../../../shared/services/households/households.service';
import { UsersService } from '../../../shared/services/users/users.service';
import { ToasterService } from '../../../shared/services/toaster/toaster.service';
import { Household, HouseholdQueryParams, GetHouseholdsResponse } from '../../../shared/models/households.model';
import { User, GetUsersResponse } from '../../../shared/services/users/users.service';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ManagerHouseholdsService } from 'src/app/modules/shared/services/manager-households/manager-households.service';
import { Manager } from 'src/app/modules/shared/services/manager-households/manager-households.model';
import { UserRoleService } from 'src/app/modules/shared/services/user-role/user-role.service';
import { AdvisorHouseholdsService } from 'src/app/modules/shared/services/advisor-households/advisor-households.service';
import { OrganizationGroupService } from 'src/app/modules/shared/services/organization-group/organization-group.service';

export enum LoadStrategy {
  InitialLoad,
  LoadMore
}

@Component({
  selector: 'app-households',
  templateUrl: './households.component.html',
  styleUrls: ['./households.component.scss']
})
export class HouseholdsComponent implements OnInit, OnDestroy {
  // Household data
  households: Household[] = [];
  /** Full unfiltered list cached after first API load — enables case-insensitive client-side search like app-household-filter. */
  householdsCache: Household[] = [];
  expandedHouseholdId: number | null = null;
  householdUsers: { [key: number]: User[] } = {};
  householdManagers: { [key: number]: Manager[] } = {};
  
  // Search and pagination
  searchQuery: string = '';
  searchIsActive = false;
  currentPage = 1;
  pageSize = 500;
  totalItems = 0;
  totalPages = 0;
  
  // Loading states
  isLoading = true;
  loadingMore = false;
  loadingUsers = false;
  loadingManagers = false;
  
  // User modal
  selectedUserId: number | null = null;
  isUserModalOpen = false;
  selectedUser: User | null = null;

  // New household modal
  isNewHouseholdModalOpen = false;

  // New user modal
  isNewUserModalOpen = false;
  selectedHouseholdId: number | null = null;

  // Manager assignment
  allManagers: User[] = [];
  selectedManagerId: number | null = null;
  isSupervisor = false;
  isAdvisor = false;
  showManagerSearch = false;
  isAssigningManager = false;
  managerSearchQuery = '';
  noManagerResults = false;
  isSearchingManagers = false;
  isManagerSearchFocused = false;
  private managerSearchSubject = new Subject<string>();
  private managerSearchSubscription: Subscription | null = null;
  private firmContextSubscription: Subscription | null = null;
  private searchTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private householdsService: HouseholdsService,
    private usersService: UsersService,
    private toaster: ToasterService,
    private router: Router,
    private managerHouseholdsService: ManagerHouseholdsService,
    private userRoleService: UserRoleService,
    private advisorHouseholdsService: AdvisorHouseholdsService,
    private organizationGroupService: OrganizationGroupService
  ) { }

  ngOnInit(): void {
    this.isSupervisor = this.userRoleService.isSupervisorOrSuperAdmin();
    this.isAdvisor = this.userRoleService.isAdvisor();
    this.getHouseholds();
    if (this.isSupervisor) {
      this.managerSearchSubscription = this.managerSearchSubject.pipe(
        debounceTime(300),
        distinctUntilChanged()
      ).subscribe(query => {
        this.loadAllManagers(query);
      });
    }

    // Refresh when the active firm changes (super admin / supervisor switches firm).
    this.firmContextSubscription = this.organizationGroupService.firmContextChanged$
      .subscribe(() => this.onFirmContextChanged());
  }

  /**
   * Reset firm-scoped UI state and re-fetch the household list. Selections
   * tied to the previous firm (expanded household, modals, cached users /
   * managers) are no longer valid in the new firm.
   */
  private onFirmContextChanged(): void {
    this.isSupervisor = this.userRoleService.isSupervisorOrSuperAdmin();
    this.isAdvisor = this.userRoleService.isAdvisor();

    this.householdsCache = [];
    this.households = [];
    this.householdUsers = {};
    this.householdManagers = {};
    this.expandedHouseholdId = null;

    this.searchQuery = '';
    this.searchIsActive = false;
    this.currentPage = 1;
    this.totalItems = 0;
    this.totalPages = 0;

    this.selectedUserId = null;
    this.isUserModalOpen = false;
    this.selectedUser = null;
    this.isNewHouseholdModalOpen = false;
    this.isNewUserModalOpen = false;
    this.selectedHouseholdId = null;

    this.resetManagerSearch();

    this.getHouseholds();
  }

  /**
   * Fetch households once (bulk load) and cache the full list. Subsequent
   * searches filter the cache client-side for case-insensitive matching —
   * same pattern as `app-household-filter` on the pending-review page.
   */
  public async getHouseholds(_strategy: LoadStrategy = LoadStrategy.InitialLoad): Promise<void> {
    this.isLoading = true;

    try {
      let fetched: Household[];

      if (this.isAdvisor) {
        const response = await this.advisorHouseholdsService.getMyAssignedHouseholds(this.pageSize, 0);
        const assignments = response?.data?.assignments ?? [];
        fetched = assignments.map(a => ({
          HOUSEHOLD_ID: a.household.HOUSEHOLD_ID,
          ORGANIZATION_ID: a.household.ORGANIZATION_ID ?? 0,
          HOUSEHOLD_NAME: a.household.HOUSEHOLD_NAME ?? '',
          CREATED_AT: a.CREATED_AT,
          UPDATED_AT: a.UPDATED_AT,
        }));
      } else {
        const params: HouseholdQueryParams = {
          page: 1,
          pageSize: this.pageSize,
        };
        const response = await this.householdsService.listHouseholds(params);
        fetched = response.households;
      }

      this.householdsCache = fetched;
      this.applySearchFilter();
    } catch (error: any) {
      this.toaster.warning(`Error fetching households: ${error?.message ?? error}`, 4000);
      console.error('Error fetching households:', error);
    } finally {
      this.isLoading = false;
      this.loadingMore = false;
    }
  }

  /**
   * Apply the current search query to the cached list. No backend call.
   */
  private applySearchFilter(): void {
    const q = this.searchQuery.trim().toLowerCase();
    this.searchIsActive = !!q;
    this.households = q
      ? this.householdsCache.filter(h => (h.HOUSEHOLD_NAME || '').toLowerCase().includes(q))
      : this.householdsCache;
    this.totalItems = this.households.length;
    this.totalPages = 1;
    this.currentPage = 1;
  }

  /**
   * Live search as user types — debounced filter against cached list.
   */
  public onSearchChange(_value: string): void {
    if (this.searchTimer !== null) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => {
      this.searchTimer = null;
      this.applySearchFilter();
    }, 150);
  }

  /**
   * Handle search submission (Enter key) — same client-side filter, no debounce.
   */
  public onSearch(): void {
    if (this.searchTimer !== null) {
      clearTimeout(this.searchTimer);
      this.searchTimer = null;
    }
    this.applySearchFilter();
  }

  /**
   * Clear search and show the full cached list.
   */
  public clearSearch(): void {
    this.searchQuery = '';
    this.onSearch();
  }

  /**
   * Reset manager search state
   */
  private resetManagerSearch(): void {
    this.showManagerSearch = false;
    this.selectedManagerId = null;
    this.managerSearchQuery = '';
    this.allManagers = [];
  }

  /**
   * Toggle household expansion to show/hide users
   */
  public async toggleHousehold(householdId: number): Promise<void> {
    if (this.expandedHouseholdId === householdId) {
      // If already expanded, collapse it
      this.expandedHouseholdId = null;
    } else {
      // Reset manager search state when expanding a different household
      this.resetManagerSearch();
      
      // Otherwise, expand it and load its users
      this.expandedHouseholdId = householdId;
      // Don't await the users call so that users and managers
      // load in parallel
      this.loadHouseholdUsers(householdId);
      if (this.isSupervisor) {
        this.householdManagers[householdId] = []; // Initialize with an empty array
        await this.loadHouseholdManagers(householdId);
      }
    }
  }

  /**
   * Load users for a specific household
   */
  private async loadHouseholdUsers(householdId: number): Promise<void> {
    // Skip if we already have the users for this household
    if (this.householdUsers[householdId]) {
      return;
    }

    this.loadingUsers = true;

    try {
      const response = await this.usersService.listUsers({
        householdId: householdId
      });
      
      this.householdUsers[householdId] = response.users;
    } catch (error: any) {
      this.toaster.warning(`Error fetching users: ${error?.message ?? error}`, 4000);
      console.error('Error fetching users:', error);
    } finally {
      this.loadingUsers = false;
    }
  }

  /**
   * Navigate to user details page
   */
  public navigateToUserDetail(user: User): void {
    this.router.navigate([`/users/household/user/${user.USER_ID}`], {
      queryParams: { origin: 'users-households' }
    });
  }

  /**
   * Navigate to manager details page
   */
  public navigateToManagerDetail(manager: Manager): void {
    this.router.navigate([`/users/household/user/${manager.USER_ID}`], {
      queryParams: { origin: 'users-households' }
    });
  }

  /**
   * Close user details modal
   */
  public closeUserModal(): void {
    this.isUserModalOpen = false;
    this.selectedUserId = null;
    this.selectedUser = null;
  }

  /**
   * Open new household modal
   */
  public openNewHouseholdModal(): void {
    this.isNewHouseholdModalOpen = true;
  }

  /**
   * Close new household modal
   */
  public closeNewHouseholdModal(): void {
    this.isNewHouseholdModalOpen = false;
  }

  /**
   * Handle successful household creation
   */
  public onHouseholdCreated(): void {
    this.closeNewHouseholdModal();
    this.getHouseholds(); // Refresh the households list
  }

  /**
   * Open new user modal for a specific household
   */
  public openNewUserModal(householdId: number): void {
    this.selectedHouseholdId = householdId;
    this.isNewUserModalOpen = true;
  }

  /**
   * Close new user modal
   */
  public closeNewUserModal(): void {
    this.isNewUserModalOpen = false;
    this.selectedHouseholdId = null;
  }

  /**
   * Handle successful user creation
   */
    /**
   * Load all managers for the assign manager dropdown
   */
  private async loadAllManagers(query?: string): Promise<void> {
    this.isSearchingManagers = true;
    this.noManagerResults = false;
    try {
      this.allManagers = await this.managerHouseholdsService.getAllManagers(query);
      this.noManagerResults = !!query && query.length >= 3 && this.allManagers.length === 0;
    } catch (error: any) {
      this.toaster.warning(`Error fetching managers: ${error?.message ?? error}`, 4000);
      console.error('Error fetching managers:', error);
    } finally {
      this.isSearchingManagers = false;
    }
  }

  /**
   * Load managers for a specific household
   */
  private async loadHouseholdManagers(householdId: number): Promise<void> {
    this.loadingManagers = true;
    try {
      this.householdManagers[householdId] = await this.managerHouseholdsService.getHouseholdManagers(householdId);
    } catch (error: any) {
      this.toaster.warning(`Error fetching household managers: ${error?.message ?? error}`, 4000);
      console.error('Error fetching household managers:', error);
    } finally {
      this.loadingManagers = false;
    }
  }

  /**
   * Assign a manager to a household
   */
  public async assignManager(householdId: number): Promise<void> {
    if (!this.selectedManagerId) {
      this.toaster.warning('Please select a manager to assign.', 4000);
      return;
    }

    this.isAssigningManager = true;
    try {
      await this.managerHouseholdsService.assignManagerToHousehold(this.selectedManagerId, householdId);
      this.toaster.success('Manager assigned successfully!', 4000);
      this.loadHouseholdManagers(householdId); // Refresh the list
      this.resetManagerSearch(); // Reset the manager search form to show "+ Add Manager" button
    } catch (error: any) {
      this.toaster.warning(`Error assigning manager: ${error?.message ?? error}`, 4000);
      console.error('Error assigning manager:', error);
    } finally {
      this.isAssigningManager = false;
    }
  }

  /**
   * Unassign a manager from a household
   */
  public onManagerSearch(query: string): void {
    if (query.length >= 3) {
      this.isSearchingManagers = true;
      this.managerSearchSubject.next(query);
    } else {
      this.allManagers = []; // Clear results if query is too short
      this.noManagerResults = false;
      this.isSearchingManagers = false;
    }
  }

  public onManagerSearchFocus(): void {
    this.isManagerSearchFocused = true;
  }

  public onManagerSearchBlur(): void {
    // Use a timeout to allow click events on the results to register
    setTimeout(() => {
      this.isManagerSearchFocused = false;
    }, 200);
  }

  public selectManager(manager: User): void {
    this.selectedManagerId = manager.USER_ID;
    this.managerSearchQuery = `${manager.FIRST_NAME} ${manager.LAST_NAME}`;
    this.allManagers = []; // Hide the results list
  }

  public toggleManagerSearch(householdId: number): void {
    this.showManagerSearch = !this.showManagerSearch;
    this.selectedManagerId = null;
    this.managerSearchQuery = '';
    this.allManagers = [];
  }

  public async unassignManager(managerId: number, householdId: number): Promise<void> {
    try {
      await this.managerHouseholdsService.unassignManagerFromHousehold(managerId, householdId);
      this.toaster.success('Manager unassigned successfully!', 4000);
      this.loadHouseholdManagers(householdId); // Refresh the list
    } catch (error: any) {
      this.toaster.warning(`Error unassigning manager: ${error?.message ?? error}`, 4000);
      console.error('Error unassigning manager:', error);
    }
  }

  ngOnDestroy(): void {
    if (this.managerSearchSubscription) {
      this.managerSearchSubscription.unsubscribe();
    }
    if (this.firmContextSubscription) {
      this.firmContextSubscription.unsubscribe();
    }
    if (this.searchTimer !== null) clearTimeout(this.searchTimer);
  }

  public onUserCreated(): void {
    this.closeNewUserModal();
    if (this.expandedHouseholdId) {
      this.loadHouseholdUsers(this.expandedHouseholdId); // Refresh the users list for the current household
    }
  }
}
